import { CanMatchFn } from '@angular/router';
import { PUBLIC_PAGE_PATHS } from '@modules/public-pages/constants/public-pages.constants';
import { ROUTE } from '@shared/constants';

const firstSegment = (path: string) => path.replace(/^\/+/, '').split('/')[0];

/**
 * Single-page paths the public shell owns **exactly** — `/privacy`, `/terms`, `/support`,
 * `/documentation`. Matched on the whole URL, not just the first segment, because `/support` is a
 * static page while `/support/documents` is the authoring console in the APP shell. Matching the
 * segment alone would hand the console's whole subtree to a shell that has no route for it.
 */
const PUBLIC_SHELL_EXACT = new Set(Object.values(PUBLIC_PAGE_PATHS).map(firstSegment));

/**
 * Prefixes whose entire subtree belongs to the public shell — the help-centre reader, plus the
 * retired `/docs` base it replaced. `/docs` no longer has a reader behind it, only a redirect, but
 * it must still be claimed here: unclaimed, the app shell would swallow the URL and the redirect
 * would never run, so links shared from the old reader would break instead of forwarding.
 * Everything below these is meant to be READ at full width.
 */
const PUBLIC_SHELL_SUBTREES = new Set([
  firstSegment(ROUTE.SUPPORT_HELP.path),
  firstSegment(ROUTE.HELP_CENTRE.path),
]);

/**
 * Claims a URL for the public shell. Both shells are mounted at the empty path, so this decides
 * which one a URL belongs to instead of leaving it to the router to try the public shell's children
 * and fall through to the app shell when none match.
 */
export const publicShellScopeGuard: CanMatchFn = (_route, segments) => {
  const first = segments[0]?.path ?? '';

  if (PUBLIC_SHELL_SUBTREES.has(first)) return true;

  // Exact only: a deeper URL under one of these segments belongs to the app shell.
  return segments.length === 1 && PUBLIC_SHELL_EXACT.has(first);
};
